import '../common/muiLicense';

import { assert, setDateTimeFormatter as coreSetDateTimeFormatter, logger, LogLevel } from '@fe/core';
import { flightLayerSetTimeSource } from '@fe/flight-layer';
import { setCopyButtonComponent, setWorkerFactory as setWeatherWorkerFactory } from '@fe/weather';
import {setWorkerFactory as setProfileWorkerFactory } from '@fe/flight-profile'
import ProfileWorker from '@fe/flight-profile/worker?worker';
import WeatherWorker from '@fe/weather/worker?worker';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'maplibre-gl/dist/maplibre-gl.css';
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import 'react-grid-layout/css/styles.css';
import { Provider as StoreProvider } from 'react-redux';
import 'react-resizable/css/styles.css';
import { RouterProvider } from 'react-router';
import '../../css/app.css';
import { ApiEnvironments as ApiEnvironment } from '../common/enums';
import CopyToClipboardButton from '../components/common/CopyToClipboardButton';
import { azureAiEnabled, hostEnvironment, loggingEnabled, selectEnvironment } from '../configs/env';
import { DateTimeFormatter } from '../formatters';
import { appInsights, AzureApplicationInsightsLogger, initInsights } from '../insights/azure-insights';
import { sendTabLogin, sendTabLogout } from '../multi-tab/multi-tab';
import { getAuthInstance } from '../services/auth/auth-instance';
import { createInstance } from '../services/axios/axios';
import { serviceOptions } from '../services/axios/axios-api-generated';
import { serviceOptions as serviceOptionsV2 } from '../services/axios/axios-api-v2-generated';
import { timeService } from '../services/time/time-service';
import Tooltips from './components/TooltipWrapper';
import { AuthProvider } from './contexts/AuthProvider';
import { router } from './router/router';
import { initializeLocalSettings, setSetting } from './state/slices/settings-slice';
import { store } from './state/store';

if (loggingEnabled) {
    logger.addConsole({ minLevel: LogLevel.debug });
}

if (azureAiEnabled) {
    initInsights();
    logger.add(new AzureApplicationInsightsLogger());
}

const client = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false
        }
    }
});

// Polyfill regex escape (ES2026)
RegExp.escape ??= (string: string) => {
    return String(string).replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
};

// Backfill for weather module, TODO: move formatters to @fe/core?
coreSetDateTimeFormatter(DateTimeFormatter);
flightLayerSetTimeSource(timeService);

setCopyButtonComponent(CopyToClipboardButton);

setWeatherWorkerFactory(() => new WeatherWorker({ name: 'fe-weather-worker' }));
setProfileWorkerFactory(ProfileWorker);

if (hostEnvironment === ApiEnvironment.dev) {
    // This is not set anywhere I think
    const api = localStorage.getItem('OverrideApiEnvironmentKey');

    if (api !== null) {
        selectEnvironment((+api as ApiEnvironment) || ApiEnvironment.dev);
    } else {
        selectEnvironment(ApiEnvironment.dev);
    }
} else {
    selectEnvironment(hostEnvironment);
}

serviceOptions.axios = createInstance();
serviceOptionsV2.axios = createInstance();

const authInstance = getAuthInstance();

authInstance.on('login-success', () => {
    logger.info('Login success');
    sendTabLogin();
    const account = authInstance.getAccount();
    assert(account);
    // If we have fpm user id store it
    if (account.fpmUserId) {
        store.dispatch(setSetting('integrationId', account.fpmUserId));
    }
    appInsights?.setAuthenticatedUserContext(account.userId, undefined, true);
});

authInstance.on('logout-success', () => {
    logger.info('Logout success');
    sendTabLogout();
    appInsights?.clearAuthenticatedUserContext();
});

store.dispatch(initializeLocalSettings());

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <QueryClientProvider client={client}>
            <StoreProvider store={store}>
                <AuthProvider instance={authInstance}>
                    <Tooltips>
                        <RouterProvider router={router} />
                    </Tooltips>
                </AuthProvider>
            </StoreProvider>
        </QueryClientProvider>
    </React.StrictMode>
);
